
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { LogIn } from "lucide-react";
import { User } from "@supabase/supabase-js";
import { AvatarUpload } from "@/components/AvatarUpload";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ProfileForm } from "@/components/profile/ProfileForm";
import { TransfersList } from "@/components/transfers/TransfersList";
import { AuthForm } from "@/components/auth/AuthForm";
import { useIsMobile } from "@/hooks/use-mobile";
import { Link } from "react-router-dom";

interface Profile {
  id: string;
  avatar_url: string | null;
  first_name: string | null;
  last_name: string | null;
}

interface DownloadHeaderProps {
  user: User | null;
  profile: Profile | null;
  transfers: any[];
  onProfileUpdate: (profile: Profile | null) => void;
  onLogout: () => Promise<void>;
  onCopyLink: (filePath: string) => Promise<void>;
  onDownload: (filePath: string) => Promise<void>;
  onDelete: (transferId: string) => Promise<void>;
}

export function DownloadHeader({
  user,
  profile,
  transfers,
  onProfileUpdate,
  onLogout,
  onCopyLink,
  onDownload,
  onDelete,
}: DownloadHeaderProps) {
  const isMobile = useIsMobile();

  return (
    <div className="relative w-full px-4 sm:px-8 py-4 flex justify-between items-center">
      <Link to="/" className="transition-opacity hover:opacity-80">
        <img 
          src="https://tv.whatstudio.es/wp-content/uploads/2025/02/logo_white.svg" 
          alt="Logo Corporativo" 
          className={`${isMobile ? 'h-[4rem]' : 'h-[7rem]'} w-auto transition-all duration-300`}
        />
      </Link>

      <Sheet>
        <SheetTrigger asChild>
          <Button 
            variant="outline" 
            className="bg-white/90 hover:bg-white transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center gap-2"
          >
            {user ? (
              <>
                <AvatarUpload 
                  userId={user.id}
                  currentAvatarUrl={profile?.avatar_url}
                  onAvatarUpdate={(url) => onProfileUpdate(profile ? {...profile, avatar_url: url} : null)}
                  size="sm"
                  showUploadButton={false}
                />
                <span className="hidden sm:inline">
                  {profile?.first_name || user.email?.split('@')[0]}
                </span>
              </>
            ) : (
              <>
                <LogIn className="mr-2 h-4 w-4" />
                <span className="hidden sm:inline">Sign In</span>
              </>
            )}
          </Button>
        </SheetTrigger>
        <SheetContent className="w-full sm:w-[600px] md:w-[800px] p-0">
          <div className="h-full flex flex-col bg-[#0A0D14]">
            <SheetHeader className="p-6 sm:p-8 border-b border-[#2E2F33]">
              <SheetTitle className="text-xl sm:text-2xl font-bold text-white">
                {user ? "Mi Cuenta" : "Bienvenido"}
              </SheetTitle>
            </SheetHeader>

            {user ? (
              <Tabs defaultValue="account" className="flex-1">
                <TabsList className="px-6 sm:px-8 grid w-full grid-cols-2 bg-[#1A1B1E] border-b border-[#2E2F33]">
                  <TabsTrigger 
                    value="account" 
                    className="text-white/60 data-[state=active]:bg-[#2E2F33] data-[state=active]:text-white"
                  >
                    Tu Cuenta
                  </TabsTrigger>
                  <TabsTrigger 
                    value="transfers"
                    className="text-white/60 data-[state=active]:bg-[#2E2F33] data-[state=active]:text-white"
                  >
                    Tus Transferencias
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="account" className="flex-1 p-6 sm:p-8 space-y-6 overflow-auto">
                  <ProfileForm 
                    userId={user.id}
                    profile={profile}
                    onProfileUpdate={onProfileUpdate}
                    email={user.email || ""}
                  />
                  <Button 
                    onClick={onLogout}
                    variant="secondary" 
                    className="w-full bg-[#00F5D0] hover:bg-[#00F5D0]/90 text-black font-medium h-11"
                  >
                    Cerrar Sesión
                  </Button>
                </TabsContent>

                <TabsContent value="transfers" className="flex-1 p-6 sm:p-8 overflow-auto">
                  <TransfersList 
                    transfers={transfers}
                    onCopy={onCopyLink}
                    onDownload={onDownload}
                    onDelete={onDelete}
                  />
                </TabsContent>
              </Tabs>
            ) : (
              <div className="flex-1">
                <AuthForm />
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
